import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import swal from 'sweetalert';
import { AuthContext } from '../context/AuthContext';
import { dhakaArea, ctgArea, savarArea } from '../helpers/Areas';

const MAX_ADDRESS_LIMIT = 2;

const AddressManagement = ({ userId, initialEditId, redirectUrl, history }) => {
  const { suMyself, fetchUserPatientAndAddress, postActivity } = useContext(AuthContext);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);

  // Form states
  const [district, setDistrict] = useState('Dhaka');
  const [thana, setThana] = useState('');
  const [addressDetails, setAddressDetails] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [areaOptions, setAreaOptions] = useState(dhakaArea);

  const getPatientId = () => {
    if (suMyself && suMyself.length > 0) {
      return suMyself[0].id;
    }
    return null;
  };

  const fetchAddresses = async () => {
    const patientId = getPatientId();
    let storedUserId = userId;
    if (!storedUserId) {
      const raw = localStorage.getItem('user_id');
      if (raw) {
        try {
          storedUserId = JSON.parse(raw);
        } catch (e) {
          storedUserId = raw;
        }
      }
    }

    if (!patientId && !storedUserId) return;

    setLoading(true);
    try {
      let combined = [];
      let seen = new Set();

      if (storedUserId) {
        try {
          const userResp = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/user_management/address/?user=${storedUserId}&status=true&ordering=-id`
          );
          if (userResp.data && userResp.data.results) {
            userResp.data.results.forEach((addr) => {
              if (addr.status && !seen.has(addr.id)) {
                seen.add(addr.id);
                combined.push(addr);
              }
            });
          }
        } catch (e) {
          console.error('Error fetching user addresses:', e);
        }
      }

      if (patientId) {
        try {
          const patResp = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/user_management/address/?patient=${patientId}&status=true&ordering=-id`
          );
          if (patResp.data && patResp.data.results) {
            patResp.data.results.forEach((addr) => {
              if (addr.status && !seen.has(addr.id)) {
                seen.add(addr.id);
                combined.push(addr);
              }
            });
          }
        } catch (e) {
          console.error('Error fetching patient addresses:', e);
        }
      }

      combined.sort((a, b) => b.id - a.id);
      // Strictly limit to latest 2 addresses for user frontend
      const topAddresses = combined.slice(0, MAX_ADDRESS_LIMIT);
      setAddresses(topAddresses);

      // If initialEditId is supplied (e.g. redirected from Checkout), automatically open edit form
      if (initialEditId) {
        const target = combined.find((a) => String(a.id) === String(initialEditId));
        if (target) {
          handleEditAddress(target);
        } else {
          // Fetch the specific address directly if not in combined top list
          try {
            const singleResp = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/user_management/address/${initialEditId}/`
            );
            if (singleResp.data) {
              handleEditAddress(singleResp.data);
            }
          } catch (err) {}
        }
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [suMyself, userId, initialEditId]);

  // Update area options when district changes
  const handleDistrictChange = (selectedDistrict) => {
    setDistrict(selectedDistrict);
    setThana('');
    if (selectedDistrict === 'Dhaka') {
      setAreaOptions(dhakaArea);
    } else if (selectedDistrict === 'Chattogram') {
      setAreaOptions(ctgArea);
    } else if (selectedDistrict === 'Savar') {
      setAreaOptions(savarArea);
    } else {
      setAreaOptions(dhakaArea);
    }
  };

  const handleOpenAddForm = () => {
    if (addresses.length >= MAX_ADDRESS_LIMIT) {
      toast.warning(`You have reached the maximum limit of ${MAX_ADDRESS_LIMIT} addresses.`);
      return;
    }
    setEditAddressId(null);
    setDistrict('Dhaka');
    setAreaOptions(dhakaArea);
    setThana('');
    setAddressDetails('');
    setMobile('');
    setEmail('');
    setShowForm(true);
    if (postActivity) {
      postActivity('button', 'Open Add Address Form', window.location.href);
    }
  };

  const handleEditAddress = (addr) => {
    setEditAddressId(addr.id);
    setDistrict(addr.district || 'Dhaka');
    if (addr.district === 'Dhaka') {
      setAreaOptions(dhakaArea);
    } else if (addr.district === 'Chattogram') {
      setAreaOptions(ctgArea);
    } else if (addr.district === 'Savar') {
      setAreaOptions(savarArea);
    } else {
      setAreaOptions(dhakaArea);
    }
    setThana(addr.thana || '');
    setAddressDetails(addr.address || '');
    setMobile(addr.mobile || '');
    setEmail(addr.email || '');
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditAddressId(null);
    setThana('');
    setAddressDetails('');
    setMobile('');
    setEmail('');
    if (redirectUrl && history) {
      history.push(redirectUrl);
    }
  };

  const handleDeleteAddress = (e, addr) => {
    e.stopPropagation();
    swal({
      title: 'Are you sure?',
      text: 'Do you want to delete this address?',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axios
          .put(`${process.env.REACT_APP_BASE_URL}/user_management/address/${addr.id}/`, {
            status: false,
          })
          .then(() => {
            toast.success('Address deleted successfully.');
            fetchAddresses();
            const raw = localStorage.getItem('user_id');
            const storedUserId = userId || (raw ? JSON.parse(raw) : null);
            if (storedUserId && fetchUserPatientAndAddress) {
              fetchUserPatientAndAddress(storedUserId);
            }
          })
          .catch(() => {
            // Fallback to delete endpoint if put status fails
            axios
              .delete(`${process.env.REACT_APP_BASE_URL}/user_management/address/${addr.id}/`)
              .then(() => {
                toast.success('Address deleted successfully.');
                fetchAddresses();
              })
              .catch((err) => {
                console.error('Delete error:', err);
                toast.error('Failed to delete address.');
              });
          });
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!district || district.trim() === '') {
      toast.error('Please select district.');
      return;
    }
    if (!thana || thana.trim() === '') {
      toast.error('Please select area / thana.');
      return;
    }
    if (!addressDetails || addressDetails.trim() === '') {
      toast.error('Please enter detailed address.');
      return;
    }
    if (!mobile || mobile.trim() === '') {
      toast.error('Please enter mobile number.');
      return;
    }

    if (!editAddressId && addresses.length >= MAX_ADDRESS_LIMIT) {
      toast.error(`You can only save up to ${MAX_ADDRESS_LIMIT} addresses. Please edit or delete an existing address.`);
      return;
    }

    const patientId = getPatientId();
    let storedUserId = userId;
    if (!storedUserId) {
      const raw = localStorage.getItem('user_id');
      if (raw) {
        try {
          storedUserId = JSON.parse(raw);
        } catch (e) {
          storedUserId = raw;
        }
      }
    }

    const payload = {
      district: district,
      thana: thana,
      address: addressDetails.trim(),
      mobile: mobile.trim(),
      email: email.trim(),
      patient: patientId || null,
      user: storedUserId || null,
      status: true,
    };

    setSubmitting(true);

    if (editAddressId) {
      axios
        .put(`${process.env.REACT_APP_BASE_URL}/user_management/address/${editAddressId}/`, payload)
        .then(() => {
          toast.success('Address updated successfully.');
          setSubmitting(false);
          handleCancelForm();
          fetchAddresses();
          if (storedUserId && fetchUserPatientAndAddress) {
            fetchUserPatientAndAddress(storedUserId);
          }
          if (redirectUrl && history) {
            setTimeout(() => {
              history.push(redirectUrl);
            }, 300);
          }
        })
        .catch((err) => {
          setSubmitting(false);
          console.error('Update address error:', err);
          toast.error('Failed to update address.');
        });
    } else {
      axios
        .post(`${process.env.REACT_APP_BASE_URL}/user_management/address/`, payload)
        .then(() => {
          toast.success('Address added successfully.');
          setSubmitting(false);
          handleCancelForm();
          fetchAddresses();
          if (storedUserId && fetchUserPatientAndAddress) {
            fetchUserPatientAndAddress(storedUserId);
          }
          if (redirectUrl && history) {
            setTimeout(() => {
              history.push(redirectUrl);
            }, 300);
          }
        })
        .catch((err) => {
          setSubmitting(false);
          console.error('Add address error:', err);
          toast.error('Failed to add address.');
        });
    }
  };

  return (
    <div className='address-management-section'>
      <div className='d-flex align-items-center justify-content-between mb-4'>
        <div>
          <h4 className='mb-1' style={{ fontWeight: 600, color: '#1b3b6f' }}>Saved Addresses</h4>
          <p className='text-muted mb-0' style={{ fontSize: '14px' }}>
            Manage your saved delivery/sample collection addresses (Max {MAX_ADDRESS_LIMIT} addresses).
          </p>
        </div>
        {!showForm && addresses.length < MAX_ADDRESS_LIMIT && (
          <button
            type='button'
            className='btn-btn-light'
            onClick={handleOpenAddForm}
            style={{
              background: '#31b5c4',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            + Add New Address
          </button>
        )}
      </div>

      {loading && (
        <div className='text-center py-4'>
          <Spinner animation='border' role='status' variant='primary'>
            <span className='sr-only'>Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Address Limit Banner */}
      {addresses.length >= MAX_ADDRESS_LIMIT && !showForm && (
        <div
          className='alert alert-info py-2 px-3 mb-4 d-flex align-items-center'
          style={{ fontSize: '13px', borderRadius: '6px' }}
        >
          <i className='fa fa-info-circle mr-2' style={{ fontSize: '16px' }}></i>
          <span>
            You have reached the maximum allowed limit of {MAX_ADDRESS_LIMIT} addresses. You can edit or delete an existing address below.
          </span>
        </div>
      )}

      {/* Address List Cards */}
      {!loading && !showForm && (
        <div className='row'>
          {addresses.length === 0 ? (
            <div className='col-12 text-center py-5'>
              <i className='fa fa-map-marker-alt text-muted mb-3' style={{ fontSize: '40px' }}></i>
              <p className='text-muted'>No saved addresses found. Click "+ Add New Address" to create one.</p>
            </div>
          ) : (
            addresses.map((addr, index) => (
              <div className='col-md-6 mb-3' key={addr.id}>
                <div
                  className='p-3 border rounded h-100 position-relative'
                  style={{
                    borderColor: '#cacad3',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div className='d-flex align-items-center justify-content-between mb-2'>
                    <div className='d-flex align-items-center'>
                      <h5 className='mb-0 font-weight-bold' style={{ fontSize: '16px', color: '#1b3b6f' }}>
                        Address {index + 1}
                      </h5>
                      {index === 0 && (
                        <span
                          className='badge badge-success ml-2'
                          style={{
                            backgroundColor: '#e6f7f8',
                            color: '#31b5c4',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                          }}
                        >
                          Latest
                        </span>
                      )}
                    </div>
                    <div>
                      <button
                        type='button'
                        className='btn btn-sm btn-link p-1 text-primary mr-2'
                        title='Edit Address'
                        onClick={() => handleEditAddress(addr)}
                        style={{ fontSize: '15px' }}
                      >
                        <i className='fa fa-edit'></i>
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-link p-1 text-danger'
                        title='Delete Address'
                        onClick={(e) => handleDeleteAddress(e, addr)}
                        style={{ fontSize: '15px' }}
                      >
                        <i className='far fa-trash-alt'></i>
                      </button>
                    </div>
                  </div>

                  <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                    <div>
                      <strong style={{ color: '#333' }}>District:</strong> {addr.district}
                    </div>
                    <div>
                      <strong style={{ color: '#333' }}>Area / Thana:</strong> {addr.thana}
                    </div>
                    <div>
                      <strong style={{ color: '#333' }}>Detailed Address:</strong> {addr.address}
                    </div>
                    <div>
                      <strong style={{ color: '#333' }}>Mobile:</strong> {addr.mobile}
                    </div>
                    {addr.email && (
                      <div>
                        <strong style={{ color: '#333' }}>Email:</strong> {addr.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add / Edit Address Form */}
      {showForm && (
        <div
          className='p-4 border rounded'
          style={{
            backgroundColor: '#fcfdfe',
            borderColor: '#31b5c4',
            borderRadius: '8px',
          }}
        >
          <h5 className='mb-3' style={{ fontWeight: 600, color: '#1b3b6f' }}>
            {editAddressId ? 'Edit Address' : 'Add New Address'}
          </h5>

          <form onSubmit={handleSubmit}>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                  District <span className='text-danger'>*</span>
                </label>
                <select
                  className='form-control'
                  value={district}
                  onChange={(e) => handleDistrictChange(e.target.value)}
                  style={{ height: '44px', borderRadius: '6px' }}
                  required
                >
                  <option value='Dhaka'>Dhaka</option>
                  <option value='Chattogram'>Chattogram</option>
                  <option value='Savar'>Savar</option>
                </select>
              </div>

              <div className='col-md-6 mb-3'>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                  Area / Thana <span className='text-danger'>*</span>
                </label>
                <select
                  className='form-control'
                  value={thana}
                  onChange={(e) => setThana(e.target.value)}
                  style={{ height: '44px', borderRadius: '6px' }}
                  required
                >
                  <option value=''>Select Area</option>
                  {areaOptions.map((area, idx) => (
                    <option key={idx} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              <div className='col-12 mb-3'>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                  Detailed Address (House, Road, Block, etc.) <span className='text-danger'>*</span>
                </label>
                <textarea
                  className='form-control'
                  rows='3'
                  placeholder='e.g., House #12, Road #4, Sector #3, Uttara'
                  value={addressDetails}
                  onChange={(e) => setAddressDetails(e.target.value)}
                  style={{ borderRadius: '6px' }}
                  required
                ></textarea>
              </div>

              <div className='col-md-6 mb-3'>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                  Mobile Number <span className='text-danger'>*</span>
                </label>
                <input
                  type='tel'
                  className='form-control'
                  placeholder='01XXXXXXXXX'
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  style={{ height: '44px', borderRadius: '6px' }}
                  required
                />
              </div>

              <div className='col-md-6 mb-3'>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#444' }}>
                  Email Address (Optional)
                </label>
                <input
                  type='email'
                  className='form-control'
                  placeholder='yourname@example.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ height: '44px', borderRadius: '6px' }}
                />
              </div>
            </div>

            <div className='d-flex align-items-center mt-3' style={{ gap: '12px' }}>
              <button
                type='submit'
                disabled={submitting}
                className='btn'
                style={{
                  background: '#31b5c4',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  minWidth: '120px',
                }}
              >
                {submitting ? (
                  <Spinner animation='border' size='sm' />
                ) : editAddressId ? (
                  'Update Address'
                ) : (
                  'Save Address'
                )}
              </button>
              <button
                type='button'
                onClick={handleCancelForm}
                disabled={submitting}
                className='btn'
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: '1px solid #ced4da',
                  backgroundColor: '#f8f9fa',
                  color: '#333333',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddressManagement;
